import React, {useRef} from 'react';
import {Controller, FieldValues, Path, UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Select, {Option} from '../common/Select';
import {getCssVariable} from '../../../utils/style.utils';
import {type TypedTranslation} from '../../../i18n';

interface IReactHookFormSelectProps<T extends FieldValues> {
    fieldName: Path<T>;
    options: Option[];
    useForm: UseFormReturn;
    placeholder?: string;
    setSearchTerm?: (term: string) => void;
    className?: string;
    disabled?: boolean;
    onChange?: (value: string) => void;
    dataCy?: string;
    onScrollToBottom?: () => void;
}

function ReactHookFormSelect<T extends FieldValues>({
    fieldName,
    useForm,
    options,
    setSearchTerm,
    placeholder,
    className,
    disabled,
    onChange,
    dataCy,
    onScrollToBottom,
}: IReactHookFormSelectProps<T>): JSX.Element {
    const {control, setValue, formState} = useForm;
    const {errors} = formState;
    const {t} = useTranslation();
    const selectedOptionRef = useRef<Option>();

    const handleChange = (e: Option | null): void => {
        selectedOptionRef.current = e ?? undefined;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setValue(fieldName, e?.value);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange && onChange(e?.value);
    };

    const resolveSelectedOption = (fieldValue: unknown): Option | undefined => {
        const fromOptions = options.find((option) => option.value === fieldValue);
        if (fromOptions) {
            selectedOptionRef.current = fromOptions;
            return fromOptions;
        }
        if (selectedOptionRef.current?.value === fieldValue) {
            return selectedOptionRef.current;
        }
        return undefined;
    };

    return (
        <div className={`${className || ''}`} style={{width: '100%'}} data-cy={dataCy}>
            <Controller
                name={fieldName}
                control={control}
                render={({field}) => {
                    const selectedOption = resolveSelectedOption(field.value);
                    const optionsWithSelected =
                        selectedOption && !options.some((option) => option.value === selectedOption.value)
                            ? [selectedOption, ...options]
                            : options;

                    return (
                        <Select
                            onInputChange={setSearchTerm ? (e) => setSearchTerm(e) : undefined}
                            options={optionsWithSelected}
                            placeholder={placeholder}
                            onChange={(e) => handleChange(e)}
                            value={selectedOption}
                            isMenuUsingPortal
                            styles={{
                                menuPortal: (base) => ({
                                    ...base,
                                    zIndex: getCssVariable('--z-index-modal'),
                                }),
                            }}
                            dontCloseMenuOnScroll
                            disabled={disabled}
                            dataCy={dataCy}
                            onScrollToBottom={onScrollToBottom}
                        />
                    );
                }}
            />
            <div className="error-msg u-flex u-h-right">
                {errors[fieldName] && <span>{t(errors[fieldName]?.message as TypedTranslation)}</span>}
            </div>
        </div>
    );
}

export default ReactHookFormSelect;
